// src/lib/constants/monetize.ts

export const MONETIZE_QUERY_KEYS = {
  all: ['monetize'] as const,
  status: (cId: number) => [...MONETIZE_QUERY_KEYS.all, 'status', cId] as const,
  catalog: (cId: number) => [...MONETIZE_QUERY_KEYS.all, 'catalog', cId] as const,
  connect: (cId: number) => [...MONETIZE_QUERY_KEYS.all, 'connect', cId] as const,
  viewer: (cId: number, email: string) => [...MONETIZE_QUERY_KEYS.all, 'viewer', cId, email] as const,
  ledger: (cId: number, vaid: string) => [...MONETIZE_QUERY_KEYS.all, 'ledger', cId, vaid] as const,
  report: (cId: number, from: string, to: string) => [...MONETIZE_QUERY_KEYS.all, 'report', cId, from, to] as const,
  previewGrant: (cId: number, scope: string) => [...MONETIZE_QUERY_KEYS.all, 'previewGrant', cId, scope] as const,
} as const;

/** The dashboard's range presets: whole UTC days ending today. */
export const REPORT_PRESETS = ['7d', '30d', '90d', '365d'] as const;
export const REPORT_PRESET_LABELS: Record<(typeof REPORT_PRESETS)[number], string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '365d': 'Last 12 months',
};
export const REPORT_PRESET_DAYS: Record<(typeof REPORT_PRESETS)[number], number> = { '7d': 7, '30d': 30, '90d': 90, '365d': 365 };

export const PRODUCT_TYPES = ['subscription', 'pass', 'rental'] as const;
export const PRODUCT_TYPE_LABELS: Record<(typeof PRODUCT_TYPES)[number], string> = {
  subscription: 'Subscription',
  pass: 'Pass',
  rental: 'Rental',
};
export const PRODUCT_TYPE_DESCRIPTIONS: Record<(typeof PRODUCT_TYPES)[number], string> = {
  subscription: 'Recurring access, monthly or yearly, with an optional free trial.',
  pass: 'One purchase for an event or season, playable through a replay window.',
  rental: 'One purchase for a play window after checkout.',
};

export const PRODUCT_STATUSES = ['draft', 'active', 'archived'] as const;
export const PRODUCT_STATUS_LABELS: Record<(typeof PRODUCT_STATUSES)[number], string> = {
  draft: 'Draft',
  active: 'Active',
  archived: 'Archived',
};
export const PRODUCT_STATUS_COLORS: Record<(typeof PRODUCT_STATUSES)[number], string> = {
  draft: 'gray',
  active: 'green',
  archived: 'gray',
};

export const PRICE_INTERVALS = ['month', 'year', 'one_time'] as const;
export const PRICE_INTERVAL_LABELS: Record<(typeof PRICE_INTERVALS)[number], string> = {
  month: 'Monthly',
  year: 'Yearly',
  one_time: 'One-time',
};
export const PRICE_STATUSES = ['active', 'archived'] as const;

export const OFFER_DURATIONS = ['once', 'repeating', 'forever'] as const;
export const OFFER_DURATION_LABELS: Record<(typeof OFFER_DURATIONS)[number], string> = {
  once: 'First payment only',
  repeating: 'A number of months',
  forever: 'Every payment',
};
export const OFFER_STATUSES = ['active', 'archived'] as const;

export const MONETIZE_CURRENCY = 'usd';

/** Scope token prefixes (`<type>:<id>`), the commerce-api wire form. */
export const SCOPE_TYPES = ['service', 'media', 'list'] as const;

// Limits mirrored from commerce-api's domain (catalog.go); the server is the authority.
export const MONETIZE_CONSTRAINTS = {
  NAME_MAX: 100,
  DESCRIPTION_MAX: 1000,
  NICKNAME_MAX: 50,
  MIN_AMOUNT_CENTS: 50,
  MAX_TRIAL_DAYS: 365,
  MIN_CONCURRENT: 1,
  MAX_CONCURRENT: 20,
  DEFAULT_CONCURRENT: 3,
  MAX_PRICES: 10,
  MAX_SCOPES: 50,
  CODE_MIN: 3,
  CODE_MAX: 32,
  MAX_OFFER_MONTHS: 36,
  MAX_REPLAY_HOURS: 8760,
  /** Bound on media options mounted in a searchable Select (CLAUDE.md: bounded combobox data). */
  MEDIA_SEARCH_MAX_OPTIONS: 50,
  REASON_MAX: 500,
} as const;

export const TEMPLATE_NAMES = ['membership', 'ticketed_live'] as const;

// ---- the viewer's ledger (mirrors commerce-api's domain; the server is the authority) ----

export const ACCESS_MODES = ['anonymous_free', 'registered_free', 'sponsor_supported', 'trial', 'subscriber', 'pass_holder', 'rental', 'promo', 'manual_comp'] as const;
export const ACCESS_MODE_LABELS: Record<(typeof ACCESS_MODES)[number], string> = {
  anonymous_free: 'Free',
  registered_free: 'Free (signed in)',
  sponsor_supported: 'Sponsor-supported',
  trial: 'Trial',
  subscriber: 'Subscriber',
  pass_holder: 'Pass',
  rental: 'Rental',
  promo: 'Promo',
  manual_comp: 'Comp',
};

/** The modes a person may hand out; paid modes come from payments only. */
export const COMP_MODES = ['manual_comp', 'trial', 'promo'] as const;
export const COMP_MODE_DESCRIPTIONS: Record<(typeof COMP_MODES)[number], string> = {
  manual_comp: 'Comp — free access you grant',
  trial: 'Trial — as if a free trial were running',
  promo: 'Promo — as if a promo code applied',
};

export const ENTITLEMENT_STATUSES = ['active', 'expired', 'revoked', 'suspended'] as const;
export const ENTITLEMENT_STATUS_LABELS: Record<(typeof ENTITLEMENT_STATUSES)[number], string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
  suspended: 'Suspended',
};
export const ENTITLEMENT_STATUS_COLORS: Record<(typeof ENTITLEMENT_STATUSES)[number], string> = {
  active: 'green',
  expired: 'gray',
  revoked: 'red',
  suspended: 'orange',
};

export const PURCHASE_STATUSES = ['pending', 'completed', 'refunded', 'disputed'] as const;
export const PURCHASE_STATUS_LABELS: Record<(typeof PURCHASE_STATUSES)[number], string> = {
  pending: 'Pending',
  completed: 'Paid',
  refunded: 'Refunded',
  disputed: 'Disputed',
};
export const PURCHASE_STATUS_COLORS: Record<(typeof PURCHASE_STATUSES)[number], string> = {
  pending: 'gray',
  completed: 'green',
  refunded: 'gray',
  disputed: 'red',
};

export const SUBSCRIPTION_STATUSES = ['incomplete', 'trialing', 'active', 'past_due', 'paused', 'canceled', 'expired'] as const;
export const SUBSCRIPTION_STATUS_LABELS: Record<(typeof SUBSCRIPTION_STATUSES)[number], string> = {
  incomplete: 'Incomplete',
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  paused: 'Paused',
  canceled: 'Canceled',
  expired: 'Expired',
};
export const SUBSCRIPTION_STATUS_COLORS: Record<(typeof SUBSCRIPTION_STATUSES)[number], string> = {
  incomplete: 'gray',
  trialing: 'blue',
  active: 'green',
  past_due: 'orange',
  paused: 'orange',
  canceled: 'gray',
  expired: 'gray',
};

/** What the history shows for an audit action; an unknown action shows as is. */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  'entitlement.comp': 'Access granted',
  'entitlement.grant': 'Access granted',
  'entitlement.extend': 'Access extended',
  'entitlement.revoke': 'Access revoked',
  'entitlement.suspend': 'Access suspended',
  'purchase.refund': 'Refund',
  'purchase.dispute': 'Dispute opened',
  'subscription.cancel': 'Subscription canceled',
};
